<script setup>
/*
    Input widget - meta widget for all variety of input types

    Components:
        primary: <vu-input>

    Widget Properties
        widget.accept
        widget.datetime
        widget.items
        widget.min
        widget.max
        widget.label
        widget.multiple
        widget.placeholder
        widget.prefix
        widget.rows
        widget.suffix
        widget.timezone
        widget.validate
        widget.value

    CSS Properties:
        background
        color
        ...
 */
import {reactive, ref, watch} from 'vue'
import {State} from '@/paks/vu-app'
import Json5 from 'json5'
import {getWidgetProps} from '@/paks/vu-dash/BoardUtils'

defineExpose({update})
const emit = defineEmits(['submit'])

const props = defineProps({
    dashboard: Object,
    design: Boolean,
    widget: Object,
})

const page = reactive({
    args: {},
    appendIcon: null,
    data: {},
    errors: [],
    key: true,
    items: [],
    prefix: null,
    prependIcon: null,
    suffix: null,
    rules: [],
    value: null,
})

const primary = ref(null)

watch(() => props.widget.errors, (value) => {
    page.errors = value
})

function update() {
    let widget = props.widget
    Object.assign(page, getWidgetProps(widget, {primary}))

    if (widget.items) {
        try {
            page.items = Json5.parse(widget.items)
        } catch (err) {
            console.log(`Cannot parse widget items`, {items: widget.items, widget})
        }
    } else {
        page.items = []
    }
    if (widget.prefix) {
        let [kind, text] = widget.prefix.split(':')
        if (kind == 'icon') {
            page.prependIcon = text
        } else {
            page.prefix = text
        }
    }
    if (widget.suffix) {
        let [kind, text] = widget.suffix.split(':')
        if (kind == 'icon') {
            page.appendIcon = text
        } else {
            page.suffix = text
        }
    }
    if (widget.icon) {
        // Overwrite prefix
        page.prependIcon = widget.icon
    }
    //  Set initial form values
    if (widget.form) {
        let form = State.cache.forms[widget.form]
        if (form) {
            let key = widget.field || widget.label?.toLowerCase()
            //  REPAIR
            if (typeof form.value != 'object') {
                form.value = {}
            }
            if (widget.input == 'switch' && !widget.value) {
                widget.value = false
            }
            if (widget.input == 'slider' && widget.value == null) {
                widget.value = widget.min || 0
            }
            if (key) {
                form.value[key] = widget.value
            } else {
                console.log('Widget ${widget.type} ${widget.input} does not have widget.field or widget.label defined')
            }
        }
    }
}

async function onClear(event, props) {
    let widget = props.widget
    widget.value = ''
    if (widget.validate) {
        if (!new RegExp(widget.validate).exec(widget.value)) {
            page.errors = ['Bad format for field']
        }
    } else {
        page.errors = []
    }
}

async function input(value, props) {
    let widget = props.widget
    if (widget.validate) {
        if (!new RegExp(widget.validate).exec(widget.value)) {
            page.errors = ['Bad format for field']
            return
        }
    }
    page.errors = []

    if (widget.form) {
        let form = State.cache.forms[widget.form]
        if (form && typeof form.value == 'object') {
            form.value[widget.field] = value
        }
    } else {
        emit('submit', props.widget)
    }
}
</script>

<template>
    <vu-input
        ref="primary"
        class="input-widget"
        v-model="widget.value"
        v-bind="page.args"
        :accept="widget.accept"
        :append-icon="page.appendIcon"
        :clearable="true"
        :datetime="widget.datetime"
        :items="page.items"
        :min="widget.min"
        :max="widget.max"
        :label="widget.label"
        :hide-details="false"
        :multiple="widget.multiple"
        :numeric="true"
        :placeholder="widget.placeholder"
        :prefix="page.prefix"
        :prepend-icon="page.prependIcon"
        :rows="widget.rows"
        :suffix="page.suffix"
        :thumb="false"
        :timezone="widget.timezone"
        :type="widget.input"
        :validate="widget.validate"
        @clear="onClear($event, props)"
        @update:modelValue="input($event, props)" 
    />
</template>

<style lang="scss">
.input-widget {
    width: 100%;
    height: calc(100% - 10px);
    padding: 8px 10px 0 10px;
    background-color: inherit;
    .v-textarea {
        .v-field-label {
            margin-left: 0 !important;
            padding-left: 0 !important;
        }
        textarea {
            padding: 16px 2px !important;
        }
    }
}
</style>
